import { SelectionModel } from "@angular/cdk/collections";
import { HttpResponse } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Select } from "@ngxs/store";
import { BehaviorSubject, combineLatest, Observable, of, Subject, throwError } from "rxjs";
import { filter, finalize, first, map, mergeMap, takeUntil, tap } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { AssignRuleComponent } from "src/app/components/assign-rule/assign-rule.component";
import { ConfirmClearRuleComponent } from "src/app/components/confirm-clear-rule/confirm-clear-rule.component";
import { TableComponent } from "src/app/components/table/table.component";
import {
  AssetRuleApiParams,
  AssetRuleForTable,
  AssetRulePatchAddRuleBody,
  AssetRulePatchRemoveRuleBody,
  Division,
  MirRule,
  MirRuleHttp,
} from "src/app/models/mir-rule.model";
import { AssetRulesService } from "src/app/services/asset-rules/asset-rules.service";
import { AuthorizedDivisionsService } from "src/app/services/authorized-divisions.service";
import { RulesService } from "src/app/services/rules/rules.service";
import { isDefined } from "src/utils/isDefined/isDefined";

export interface RestoreAssetsPageContext {
	assetRuleApiParams: AssetRuleApiParams;
}

export interface AssetsTableClickEvent {
	asset: AssetRuleApiParams;
	context: RestoreAssetsPageContext;
}

@Component({
	selector: "app-assets-table",
	templateUrl: "./assets-table.component.html",
	styleUrls: ["./assets-table.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class AssetsTableComponent implements OnInit, OnDestroy, OnChanges {
	constructor(
		private assetRulesService: AssetRulesService,
		private rulesService: RulesService,
		private dialog: MatDialog,
		private authDivSvc: AuthorizedDivisionsService,
	) {}
	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Input() context: RestoreAssetsPageContext;
	@Output() rowClicked = new EventEmitter<AssetsTableClickEvent>();
	@Output() selectedRowsEmitter = new EventEmitter<MirRule[]>();
	@Output() clearRuleClicked = new EventEmitter<void>();

	@ViewChild("assetRuleErrors") ruleErrorTemplate: TemplateRef<any>;
	@ViewChildren(MatCheckbox) checkboxes: QueryList<MatCheckbox>;
	@ViewChild(TableComponent) table: TableComponent;

	dataSource: MatTableDataSource<any>;
	private onDestroy$ = new Subject<void>();

	pageIndex = 0;
	pageSize = 10;
	pageSizeOptions = [10, 25, 50, 75, 100];
	selectionModel: SelectionModel<any>;
	sort: MatSort;
	sortActive = "ruleName";
	sortDirection = "asc";
	paginatorLength = 0;
	view = "assets";
	selectedRows: AssetRuleForTable[] = [];
	companyId: string = null;
	divisions: Division[] = [];
	ruleIds: string[] = [];
	failedRuleNames: string;
	error = false;
	errorMessage = "An error occurred.";

	private _assetRuleApiParams$: BehaviorSubject<AssetRuleApiParams> = new BehaviorSubject({
		page: "1",
		perPage: this.pageSize.toString(),
	});

	currentAssetRuleApiParams: AssetRuleApiParams = {
		page: "1",
		perPage: this.pageSize.toString(),
		sort: "ruleName",
		sortDir: "asc",
	};

	getNewParams(divisionIds: string[], params: AssetRuleApiParams) {
		if (divisionIds.length && !params.divisionIds && !params.divisionIds?.length) {
			params = {
				...params,
				divisionIds,
			};
		}
		return params;
	}

	ngOnInit(): void {
		this.dataSource = new MatTableDataSource();
		combineLatest([
			this.selectedCompanyId$,
			this._assetRuleApiParams$,
			this.authDivSvc.divisions$.pipe(filter(isDefined)),
		])
			.pipe(
				filter(([companyId]) => isDefined(companyId)),
				mergeMap(([companyId, assetRuleParams, divisionIds]) => {
					this.companyId = companyId;
					const newParams: AssetRuleApiParams = {
						...this.currentAssetRuleApiParams,
						...assetRuleParams,
						companyId,
					};
					const paramsWithDivs = this.getNewParams(divisionIds, newParams);
					return this.assetRulesService.getAssets(paramsWithDivs);
				}),
				mergeMap(assetsHttpResponse => {
					return this.matchRulesWithAssets(assetsHttpResponse, this.companyId);
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(
				response => {
					this.paginatorLength = parseInt((response as any).headers.get("X-Total-Count"));

					this.dataSource.data = response.body;
				},
				err => {
					console.log("Error fetching assets");
					console.log(err);
					this.error = true;
				},
			);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.context.currentValue) {
			this.sortActive = changes.context.currentValue.assetRuleApiParams.sort;
			this.sortDirection = changes.context.currentValue.assetRuleApiParams.sortDir;
			this.currentAssetRuleApiParams = { ...changes.context.currentValue.assetRuleApiParams };
			this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
		}
	}

	matchRulesWithAssets(assetsHttpResponse: Partial<HttpResponse<AssetRuleApiParams[]>>, companyId: string) {
		const newAssetsHttpResponse = assetsHttpResponse.clone();
		const obs$: Observable<MirRuleHttp[]> = this.rulesService.getAllRules(companyId);

		return obs$.pipe(
			filter(isDefined),
			map(rules => {
				rules.forEach(r => {
					const matchingAssets = newAssetsHttpResponse.body.filter(asset => asset.ruleId === r.id);
					matchingAssets.length && matchingAssets.forEach(a => ((a as any).rule = r));
				});
				return newAssetsHttpResponse as Partial<HttpResponse<AssetRuleForTable[]>>;
			}),
			takeUntil(this.onDestroy$),
		);
	}

	selectedRowsReceived(rows: AssetRuleApiParams[]) {
		this.selectedRows = rows;
		const rules = this.selectedRows.map(row => row.rule);
		this.selectedRowsEmitter.emit(rules);
	}

	getDataSource() {
		return this.dataSource;
	}

	onMatSortChange(event: Sort) {
		this.selectionModel?.clear();

		this.currentAssetRuleApiParams = {
			...this.currentAssetRuleApiParams,
			sort: event.active,
			sortDir: event.direction,
		};
		this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
	}

	receiveSortObj(sort: MatSort) {
		this.sort = sort;
		this.sort.disableClear = true;
		this.getDataSource().sort = sort;
	}

	pageQueryChange(e) {
		const page = e.get("page");
		const perPage = e.get("perPage");
		this.currentAssetRuleApiParams = {
			...this.currentAssetRuleApiParams,
			page: page.toString(),
			perPage: perPage.toString(),
		};
		this.selectedRows = [];
		this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
	}

	inputChanged(search: string) {
		this.currentAssetRuleApiParams = {
			...this.currentAssetRuleApiParams,
			search,
			page: "1",
		};

		this.selectedRows = [];

		this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
	}

	private _clearRules() {
		// filter out assets that have rules attached
		const assetsWithRules = this.selectedRows.filter(row => row.rule);

		// get a de-duped list of all possible rules
		const oldRules = assetsWithRules
			.map(row => row.rule)
			.filter((rule, index, array) => array.findIndex(r => r.id === rule.id) === index);

		const assetIds = this.selectedRows.map(r => r.assetId);

		if (oldRules.length) {
			this.dialog.open(ConfirmClearRuleComponent, {
				data: {
					message: `You are about to unassign rules from ${assetsWithRules.length} asset(s) that already have rules. Click OK if you wish to continue.`,
					noRulesToClear: false,
				},
				autoFocus: true,
				restoreFocus: true,
			});

			this.assetRulesService.confirmClearRule$
				.pipe(
					first(),
					mergeMap(() => {
						if (oldRules.length) {
							const removeBody: AssetRulePatchRemoveRuleBody = {};

							oldRules.forEach(oldRule => {
								removeBody[oldRule.id] = {
									remove: assetIds,
								};
							});

							return this.assetRulesService.patchRuleAssets(this.companyId, removeBody);
						}

						return of(null);
					}),
					finalize(() => {
						this._assetRuleApiParams$.next({ ...this.currentAssetRuleApiParams });
						this.selectedRows = [];
						this.table?.selectionModel?.clear();
						this.clearRuleClicked.emit();
					}),

					takeUntil(this.onDestroy$),
				)
				.subscribe(
					() => {},
					_ => {
						this.showErrorDialog("An error occurred clearing the rules.");
					},
				);
		} else {
			this.dialog.open(ConfirmClearRuleComponent, {
				data: {
					message: "The selected assets already have no rules assigned.",
					noRulesToClear: true,
				},
				autoFocus: true,
				restoreFocus: true,
			});
		}
	}

	clearRuleClick() {
		this._clearRules();
	}

	assignRuleClick() {
		this._assignRule();
	}

	private _assignRule() {
		let _newRuleId: string;

		let _ruleExists: boolean;

		// all asset ids
		const allAssetIds = this.selectedRows.map(row => row.assetId);
		// list of deduped former rules of the selected rows

		const oldRules: MirRule[] = this.selectedRows
			.filter(row => row.rule)
			.map(row => row.rule)
			.filter((rule, index, array) => array.findIndex(r => r.id === rule.id) === index);

		// open dialog for user to confirm they want to move forward
		this.dialog.open(AssignRuleComponent, {
			data: this.companyId,
			autoFocus: true,
			restoreFocus: true,
		});

		combineLatest([
			this.assetRulesService.confirmAssignRule$.pipe(filter(isDefined)),
			this.rulesService.dispatchUpdatedRules$,
		])
			.pipe(
				filter(([newRuleId, rules]) => isDefined(newRuleId) && rules?.length > 0),
				first(),
				tap(([newRuleId, rules]) => {
					_newRuleId = newRuleId;
					_ruleExists = Boolean(rules.find(r => r.id === newRuleId));
				}),
				mergeMap(() => {
					// err out if we find that the selected id somehow doesn't exist in rules. Rare, but a good edge case
					if (!_ruleExists) {
						return throwError(`There is no rule that matches with the given id ${_newRuleId}`);
					}

					// STEP 1: create the body that adds all the selected assets to the new rule

					const addBody: AssetRulePatchAddRuleBody = {};

					addBody[_newRuleId] = {
						add: allAssetIds,
					};

					return of(addBody);
				}),
				mergeMap(addBody => {
					// STEP 2: Next, remove selected assets from their old rules and xfer to new rule

					let removeBody = null;
					if (oldRules.length) {
						removeBody = {};
						oldRules.forEach(oldRule => {
							if (oldRule.id !== _newRuleId) {
								const _assets = oldRule.assets.filter(assetId => allAssetIds.includes(assetId));
								removeBody[oldRule.id] = {
									remove: _assets,
								};
							}
						});

						return this.assetRulesService.patchRuleAssets(this.companyId, removeBody, addBody);
					}

					return this.assetRulesService.patchRuleAssets(this.companyId, null, addBody);
				}),
				finalize(() => {
					this._assetRuleApiParams$.next({ ...this.currentAssetRuleApiParams });
					this.selectedRows = [];
					this.table?.selectionModel?.clear();
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(
				() => {},
				error => {
					console.log(error);
					this.showErrorDialog("An error occurred trying to assign rules.");
				},
			);
	}

	showErrorDialog(errorMessage: string) {
		this.errorMessage = errorMessage;
		const dialogRef = this.dialog.open(this.ruleErrorTemplate);
		// close dialog after 10s
		setTimeout(() => {
			dialogRef.close();
		}, 10000);
	}

	ngOnDestroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	getDivisionIds(ids: string[]) {
		this.currentAssetRuleApiParams = {
			...this.currentAssetRuleApiParams,
			divisionIds: ids,
			page: "1",
		};

		this.selectedRows = [];

		this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
	}

	getRuleIds(ids: string[]) {
		this.currentAssetRuleApiParams = {
			...this.currentAssetRuleApiParams,
			ruleIds: ids,
			page: "1",
		};

		this.ruleIds = ids;
		this.selectedRows = [];

		this._assetRuleApiParams$.next(this.currentAssetRuleApiParams);
	}

	onRowClick(asset: AssetRuleApiParams) {
		this.rowClicked.emit({
			asset,
			context: { assetRuleApiParams: { ...this.currentAssetRuleApiParams } },
		});
	}
}
